import axios from 'axios';
import { ethers } from 'ethers';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const api = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });

// ─────────────────────────────────────────────────────────────
// USER session token (wallet login JWT)
// ─────────────────────────────────────────────────────────────
export const setUserToken = (t) => { localStorage.setItem('robo-user-token', t); };
export const getUserToken = () => localStorage.getItem('robo-user-token');
export const clearUserToken = () => { localStorage.removeItem('robo-user-token'); };

// Attach the user JWT to every request — UNLESS the request already carries an
// Authorization header (admin calls set their own via aH(), don't clobber it).
api.interceptors.request.use((config) => {
  if (!config.headers?.Authorization) {
    const t = getUserToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// If the server rejects a USER (non-admin) request as unauthorized, the session
// is dead — clear it and let the app redirect to reconnect.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err.config?.url || '';
      if (!url.includes('/admin/')) {
        clearUserToken();
        window.dispatchEvent(new Event('robo-auth-expired'));
      }
    }
    return Promise.reject(err);
  }
);

// Admin token
export const setAdminToken = (t) => { localStorage.setItem('robo-admin-token', t); };
export const getAdminToken = () => localStorage.getItem('robo-admin-token');
export const clearAdminToken = () => { localStorage.removeItem('robo-admin-token'); };
const aH = () => { const t = getAdminToken(); return t ? { Authorization: `Bearer ${t}` } : {}; };

// ─────────────────────────────────────────────────────────────
// Wallet-signature auth for funds-moving endpoints.
// ─────────────────────────────────────────────────────────────
const signWithMetaMask = async (message) => {
  if (!window.ethereum) throw new Error('MetaMask not detected');
  const provider = new ethers.BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  return signer.signMessage(message);
};

const signAndPost = async (path, action, body = {}) => {
  const wallet = (body.walletAddress || body.senderWallet || '').toLowerCase();
  if (!wallet) throw new Error('Wallet address missing');

  const { nonce } = (await api.get(`/trade/nonce/${wallet}`)).data;
  const message = `ROBO Token\nAction: ${action}\nWallet: ${wallet}\nNonce: ${nonce}`;
  const signature = await signWithMetaMask(message);

  return (await api.post(path, { ...body, walletAddress: wallet, signature, message })).data;
};

// ─────────────────────────────────────────────────────────────
// LOGIN — one signature proves wallet ownership, returns a session JWT.
// Reuses the same nonce + verifyWalletAuth machinery as funds actions.
// A view-only wallet cannot sign → cannot log in.
// ─────────────────────────────────────────────────────────────
export const loginWithWallet = async (walletAddress) => {
  const wallet = (walletAddress || '').toLowerCase();
  if (!wallet) throw new Error('Wallet address missing');

  const { nonce } = (await api.get(`/trade/nonce/${wallet}`)).data;
  const message = `ROBO Token\nAction: LOGIN\nWallet: ${wallet}\nNonce: ${nonce}`;
  const signature = await signWithMetaMask(message);

  const res = (await api.post('/auth/login', { walletAddress: wallet, signature, message })).data;
  if (res?.token) setUserToken(res.token);
  return res;
};

// Auth
export const checkWallet = async (w) => (await api.post('/auth/check-wallet', { walletAddress: w })).data;
export const validateReferral = async (c) => (await api.get(`/auth/validate-referral/${c}`)).data;
export const registerUser = async (b) => {
  const res = (await api.post('/auth/register', b)).data;
  if (res?.token) setUserToken(res.token);   // logged in right after signup
  return res;
};
export const getUserProfile = async (w) => (await api.get(`/auth/user/${w}`)).data;

// Trade
export const buyToken = async (b) => (await api.post('/trade/buy', b)).data;
// Record the payment durably the instant it is broadcast (before the main /buy
// call). Best-effort: never let a logging failure block the actual purchase.
export const submitBuyIntent = async (b) => {
  try { return (await api.post('/trade/buy/submit', b)).data; }
  catch (e) { return { success: false }; }
};
export const sellToken = async (b) => signAndPost('/trade/sell', 'SELL', b);
export const getPrice = async () => (await api.get('/trade/price')).data;

// Appearance (public): theme accent + active notification popup
export const getAppearance = async () => (await api.get('/setup/appearance')).data;
export const getPriceHistory = async (days = 30) => (await api.get(`/trade/price-history?days=${days}`)).data;
export const getBalance = async (w) => (await api.get(`/trade/balance/${w}`)).data;
export const getHistory = async (w, t, page = 1, limit = 15) => {
  const params = new URLSearchParams({ page, limit });
  if (t) params.append('type', t);
  return (await api.get(`/trade/history/${w}?${params}`)).data;
};
export const getLevelIncomeHistory = async (w, page = 1, limit = 15) => (await api.get(`/trade/level-income-history/${w}?page=${page}&limit=${limit}`)).data;

// Claim (user side)
export const claimLevelIncome = async (b) => signAndPost('/trade/claim-level-income', 'CLAIM', b);
export const getMyClaimRequests = async (w, page = 1) => (await api.get(`/trade/claim-requests/${w}?page=${page}`)).data;

// Sell Withdrawal (user side)
export const requestSellWithdrawal = async (b) => signAndPost('/trade/sell-withdraw', 'SELL_WITHDRAW', b);
export const getMySellWithdrawals = async (w, page = 1) => (await api.get(`/trade/sell-withdrawals/${w}?page=${page}`)).data;

// Royalty (user side)
export const getMyRoyalty = async (w) => (await api.get(`/trade/royalty/${w}`)).data;
export const claimRoyalty = async (b) => signAndPost('/trade/royalty/claim', 'ROYALTY_CLAIM', b);

// Network
export const getDirectReferrals = async (w) => (await api.get(`/network/direct/${w}`)).data;
export const getTree = async (w, m = 10) => (await api.get(`/network/tree/${w}?maxLevel=${m}`)).data;
export const getLevelCounts = async (w, m = 10) => (await api.get(`/network/level-counts/${w}?maxLevel=${m}`)).data;
export const getTeamSize = async (w) => (await api.get(`/network/team-size/${w}`)).data;
export const getUpline = async (w) => (await api.get(`/network/upline/${w}`)).data;
export const getLevelSummary = async (w) => (await api.get(`/network/level-summary/${w}`)).data;

// Admin Auth
export const adminLogin = async (u, p) => (await api.post('/admin/login', { username: u, password: p })).data;
export const adminSeed = async () => (await api.post('/admin/seed')).data;
export const verifyAdminAccess = async () => (await api.get('/admin/verify', { headers: aH() })).data;
export const changeAdminPassword = async (b) => (await api.put('/admin/change-password', b, { headers: aH() })).data;

// Admin Config
export const getAdminConfig = async () => (await api.get('/admin/config', { headers: aH() })).data;
export const getPendingCounts = async () => (await api.get('/admin/pending-counts', { headers: aH() })).data;
export const updateAdminConfig = async (b) => (await api.put('/admin/config', b, { headers: aH() })).data;
export const setInitialPrice = async (b) => (await api.post('/admin/set-initial-price', b, { headers: aH() })).data;
export const updateLevelConfig = async (b) => (await api.put('/admin/update-level-config', b, { headers: aH() })).data;
export const addCurrency = async (b) => (await api.post('/admin/add-currency', b, { headers: aH() })).data;
export const updateCurrency = async (s, b) => (await api.put(`/admin/update-currency/${s}`, b, { headers: aH() })).data;
export const deleteCurrency = async (s) => (await api.delete(`/admin/delete-currency/${s}`, { headers: aH() })).data;
export const setContract = async (b) => (await api.post('/admin/set-contract', b, { headers: aH() })).data;
export const updateNotification = async (b) => (await api.put('/admin/notification', b, { headers: aH() })).data;
// Test console
export const testBumpPrice = async (b) => (await api.post('/admin/test/bump-price', b, { headers: aH() })).data;
export const testBackdateBuyPeriod = async (b) => (await api.post('/admin/test/backdate-buy-period', b, { headers: aH() })).data;
export const testResetUser = async (b) => (await api.post('/admin/test/reset-user', b, { headers: aH() })).data;
export const testResetAll = async (b) => (await api.post('/admin/test/reset-all', b || {}, { headers: aH() })).data;
export const toggleBuy = async () => (await api.post('/admin/toggle-buy', {}, { headers: aH() })).data;
export const toggleSell = async () => (await api.post('/admin/toggle-sell', {}, { headers: aH() })).data;
export const toggleTransfer = async () => (await api.post('/admin/toggle-transfer', {}, { headers: aH() })).data;
export const toggleP2P = async () => (await api.post('/admin/toggle-p2p', {}, { headers: aH() })).data;
export const toggleClaim = async () => (await api.post('/admin/toggle-claim', {}, { headers: aH() })).data;

// Admin User Management
export const getPlatformStats = async () => (await api.get('/admin/stats', { headers: aH() })).data;
export const adminListUsers = async (s = '', p = 1, l = 20) => (await api.get(`/admin/users?search=${s}&page=${p}&limit=${l}`, { headers: aH() })).data;
export const adminGetUser = async (id) => (await api.get(`/admin/users/${id}`, { headers: aH() })).data;
export const adminGetUserTree = async (id, m = 10) => (await api.get(`/admin/users/${id}/tree?maxLevel=${m}`, { headers: aH() })).data;
export const adminGetUserTransactions = async (id, p = 1, l = 20, t) => (await api.get(`/admin/users/${id}/transactions?page=${p}&limit=${l}${t ? `&type=${t}` : ''}`, { headers: aH() })).data;

// Admin: read-only "view as user" — returns a user session token for this user
export const adminImpersonateUser = async (roboId) => (await api.get(`/admin/users/${roboId}/impersonate`, { headers: aH() })).data;

// Admin: ALL transactions across every user (newest first, referral + upline codes, txHash)
export const getAllAdminTransactions = async (p = 1, l = 20, type, search) => {
  const params = new URLSearchParams({ page: String(p), limit: String(l) });
  if (type) params.set('type', type);
  if (search) params.set('search', search);
  return (await api.get(`/admin/transactions?${params.toString()}`, { headers: aH() })).data;
};

// Admin Ledger
export const getAdminLedger = async (type, user, p = 1, limit = 30) => {
  const params = new URLSearchParams({ page: p, limit });
  if (type) params.append('type', type);
  if (user) params.append('user', user);
  return (await api.get(`/admin/ledger?${params}`, { headers: aH() })).data;
};
export const getAdminLedgerSummary = async () => (await api.get('/admin/ledger/summary', { headers: aH() })).data;

// Admin Claims
export const getAdminClaims = async (status, p = 1) => (await api.get(`/admin/claims?${status ? `status=${status}&` : ''}page=${p}`, { headers: aH() })).data;
export const approveClaim = async (id, txHash) => (await api.post(`/admin/claims/${id}/approve`, { txHash }, { headers: aH() })).data;
export const rejectClaim = async (id, reason) => (await api.post(`/admin/claims/${id}/reject`, { reason }, { headers: aH() })).data;

// Admin Sell Withdrawals
export const getSellWithdrawals = async (status, p = 1) => (await api.get(`/admin/sell-withdrawals?${status ? `status=${status}&` : ''}page=${p}`, { headers: aH() })).data;
export const approveSellWithdrawal = async (id) => (await api.post(`/admin/sell-withdrawals/${id}/approve`, {}, { headers: aH() })).data;
export const rejectSellWithdrawal = async (id, reason) => (await api.post(`/admin/sell-withdrawals/${id}/reject`, { reason }, { headers: aH() })).data;

// Admin Royalty
export const getRoyaltyClaims = async (status, p = 1) => (await api.get(`/admin/royalty/claims?${status ? `status=${status}&` : ''}page=${p}`, { headers: aH() })).data;
export const approveRoyaltyClaim = async (id, reward) => (await api.post(`/admin/royalty/claims/${id}/approve`, reward, { headers: aH() })).data;
export const rejectRoyaltyClaim = async (id, reason) => (await api.post(`/admin/royalty/claims/${id}/reject`, { reason }, { headers: aH() })).data;
export const getRoyaltyConfig = async () => (await api.get('/admin/royalty/config', { headers: aH() })).data;
export const getRoyaltyEligible = async (period = '', page = 1) => (await api.get(`/admin/royalty/eligible?page=${page}${period ? `&period=${period}` : ''}`, { headers: aH() })).data;
export const scanRoyaltyEligibility = async () => (await api.post('/admin/royalty/scan', {}, { headers: aH() })).data;
export const distributeRoyalty = async (period = '', commit = false) => (await api.post('/admin/royalty/distribute', { period, commit }, { headers: aH() })).data;
export const getRoyaltyDistributions = async () => (await api.get('/admin/royalty/distributions', { headers: aH() })).data;
export const updateRoyaltyConfig = async (b) => (await api.put('/admin/royalty/config', b, { headers: aH() })).data;
export const previewUserRoyalty = async (roboId) => (await api.get(`/admin/royalty/preview/${roboId}`, { headers: aH() })).data;

export const getSpendableWallets = async (search = '', page = 1) => (await api.get(`/admin/spend/wallets?search=${encodeURIComponent(search)}&page=${page}`, { headers: aH() })).data;
export const getSpendWalletDetail = async (w) => (await api.get(`/admin/spend/wallet/${w}`, { headers: aH() })).data;
export const executeSpend = async (data) => (await api.post('/admin/spend/execute', data, { headers: aH() })).data;
export const getSpendLogs = async (page = 1) => (await api.get(`/admin/spend/logs?page=${page}`, { headers: aH() })).data;

// Wallet management
export const getSystemWalletBalances = async () => (await api.get('/admin/wallets/balances', { headers: aH() })).data;
export const adminTransferFunds = async (data) => (await api.post('/admin/wallets/transfer', data, { headers: aH() })).data;

// P2P Transfer (signed)
export const p2pTransfer = async (data) => signAndPost('/trade/p2p-transfer', 'P2P_TRANSFER', data);
export const p2pInvest = async (data) => signAndPost('/trade/p2p-invest', 'P2P_INVEST', data);

// Wallet transfer logs
export const getWalletTransferLogs = async (status, label, page = 1) => {
  const params = new URLSearchParams({ page });
  if (status) params.append('status', status);
  if (label) params.append('label', label);
  return (await api.get(`/admin/wallets/logs?${params}`, { headers: aH() })).data;
};

export default api;